import React from 'react';
import { DefaultPanelHeader } from '../DefaultPanelHeader';
import SidePanel from '@monofront/design-system/organisms/SidePanel';
import { DeliveryBarMachineContext } from '@monofront/business/contexts';
import { ShippingMethodSelector } from '../ShippingMethodSelectionPanel/ShippingMethodSelector/ShippingMethodSelector';
import { ShippingMethodTypes } from '@monofront/types';
import { useShippingMethodInformationPanel } from '../ShippingMethodSelectionPanel/useShippingMethodInformationPanel';
import { Spacer } from '../ShippingMethodSelectionPanel/Spacer';
import { DeliveryAddressDisplay } from './DeliveryAddressDisplay';
import { useTranslations } from 'next-intl';
import { useLocalOrRemoteCustomer } from '@hooks/useLocalOrRemoteCustomer';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar/events';
import invariant from 'tiny-invariant';

type SelectedShippingMethodInformationPanelProps = {
  closePanel: () => void;
};

export const SelectedShippingMethodInformationPanel: React.FC<
  SelectedShippingMethodInformationPanelProps
> = ({ closePanel }) => {
  const { send } = DeliveryBarMachineContext.useActorRef();
  const tGlobal = useTranslations('Global');
  const tShipping = useTranslations('Shipping');
  const tAccount = useTranslations('Account');
  const tCart = useTranslations('Cart');
  const tDeliveryBar = useTranslations('DeliveryBar.CTA.shippingTypeTitle');
  const { resolvedCustomer: customer } = useLocalOrRemoteCustomer();

  const preferredShippingMethod = customer?.preferredShippingMethod;

  const {
    shippingMethodSelectionContent,
    shippingMethods,
    slots,
    descriptionSerializer,
    subtitleSerializer,
    findEligibleShippingMethod,
  } = useShippingMethodInformationPanel();

  const selectedShippingMethod = shippingMethods?.find(
    (shippingMethod) => shippingMethod.type === preferredShippingMethod?.type,
  );

  const selectedShippingMethodData =
    shippingMethodSelectionContent?.shippingMethods.find(
      ({ shippingMethod }) =>
        shippingMethod?.slug === preferredShippingMethod?.type,
    )?.shippingMethod;

  const eligibleShippingMethod = selectedShippingMethod?.type
    ? findEligibleShippingMethod(selectedShippingMethod.type)
    : undefined;

  const isCourier =
    selectedShippingMethod?.type === ShippingMethodTypes.COURIER;
  const isHome = selectedShippingMethod?.type === ShippingMethodTypes.HOME;

  if (!eligibleShippingMethod) {
    return null;
  }

  const shippingMethodTypeValue = selectedShippingMethod?.type;

  invariant(
    shippingMethodTypeValue &&
      Object.values(ShippingMethodTypes).includes(
        shippingMethodTypeValue as ShippingMethodTypes,
      ),
    'Selected shipping method type must be a valid ShippingMethodTypes value',
  );

  const shippingMethodType = shippingMethodTypeValue as ShippingMethodTypes;

  const changeShippingMethod = () => {
    send({
      type: EVENTS.GO_TO_STATE_SHIPPING_METHOD_SELECTOR,
    });
  };

  const changeAddress = () => {
    send({
      type: EVENTS.GO_TO_STATE_ADDRESS_SELECTOR,
    });
  };

  return (
    <>
      <DefaultPanelHeader
        title={tCart('myDelivery')}
        showGoBackButton={false}
        onClose={closePanel}
      />
      <SidePanel.SidePanelBody UNSAFE_className="!tw-px-4 !tw-py-6">
        <ShippingMethodSelector
          shippingMethodType={shippingMethodType}
          carrierId={selectedShippingMethodData?.carrierId ?? ''}
          title={selectedShippingMethodData?.data?.title}
          subtitle={subtitleSerializer(
            selectedShippingMethodData?.data?.subtitle,
            slots,
          )}
          description={descriptionSerializer(
            selectedShippingMethod?.configurations ?? [],
            selectedShippingMethodData?.data?.description,
          )}
          labels={selectedShippingMethodData?.data?.labels}
          eligibleShippingMethod={eligibleShippingMethod}
          mode="information"
        />
        <Spacer className="tw-my-8" />
        <div className="tw-space-y-2">
          <h3 className="tw-lf-title-xs">
            {isCourier || isHome
              ? tAccount('deliveryAddress')
              : tDeliveryBar('pickup')}
          </h3>
          <DeliveryAddressDisplay />
          <button
            onClick={changeAddress}
            className="tw-lf-button tw-lf-button-secondary tw-lf-body-bold-sm"
          >
            {tGlobal('update')}
          </button>
        </div>
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-bg-white tw-p-4">
          <button
            onClick={changeShippingMethod}
            className={`tw-lf-button tw-lf-button-primary tw-lf-button-md tw-w-full`}
          >
            {tShipping('updateDeliveryMethod')}
          </button>
        </div>
      </SidePanel.SidePanelBody>
    </>
  );
};
